import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Copy, Check, RefreshCw, ExternalLink, Plus, Trash2, Layers, Search, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import GenerateModal from '../components/feeds/GenerateModal';
import AppLayout from '../components/AppLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import RecordModal from '../components/RecordModal';
import { StatusBadge, QualityBar, FormatBadge, fmtNum } from '../components/feeds/shared';
import MappingTab from '../components/feeds/MappingTab';
import RulesTab from '../components/feeds/RulesTab';
import { api } from '../api/feedy';
import { errorMessage } from '../utils/notify';

export default function FeedEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [channels, setChannels] = useState([]);
  const [sources, setSources] = useState([]);
  const [internalFields, setInternalFields] = useState([]);
  const [tab, setTab] = useState('general');
  const [focusRuleId, setFocusRuleId] = useState(null); // Excluded tab → "Edit rule" highlights it in Rules
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [genOpen, setGenOpen] = useState(false);
  const [genTrigger, setGenTrigger] = useState(0);

  const [loadError, setLoadError] = useState('');
  const loadFeed = useCallback(() => api.feeds.get(id).then((f) => { setFeed(f); setLoadError(''); })
    .catch((err) => setLoadError(err?.response?.status === 404 ? 'This feed does not exist (any more).' : errorMessage(err))), [id]);
  useEffect(() => {
    loadFeed();
    api.channels.list().then(setChannels).catch(() => {});
    api.sources.list().then(setSources).catch(() => {});
    api.items.fields().then(setInternalFields).catch(() => {});
  }, [id, loadFeed]);

  const channelSpec = channels.find((c) => c.key === feed?.channel);
  const channelFields = channelSpec?.fields || [];

  const runPreview = useCallback((q) => {
    setPreviewLoading(true);
    api.feeds.preview(id, q ? { q } : {}).then(setPreview).finally(() => setPreviewLoading(false));
  }, [id]);
  useEffect(() => { if ((tab === 'quality' || tab === 'preview') && !preview && feed) runPreview(); }, [tab, preview, feed, runPreview]);

  // Full quality (all records) comes from the latest generation run, not the 25-row sample.
  const [runReport, setRunReport] = useState(null);
  useEffect(() => {
    if (tab === 'quality' && feed) api.feeds.runs(id).then((r) => setRunReport(r[0]?.report || null)).catch(() => {});
  }, [tab, feed, id]);

  // Excluded products (base rows that didn't make it into the feed). Read
  // instantly from the latest generation run's recorded report; loaded lazily
  // when the tab is first opened.
  const [excluded, setExcluded] = useState(null);
  const [excludedLoading, setExcludedLoading] = useState(false);
  const loadExcluded = useCallback(() => {
    setExcludedLoading(true);
    api.feeds.excluded(id, { limit: 2000 })
      .then(setExcluded)
      .catch(() => setExcluded({ error: true }))
      .finally(() => setExcludedLoading(false));
  }, [id]);
  useEffect(() => { if (tab === 'excluded' && !excluded && feed) loadExcluded(); }, [tab, excluded, feed, loadExcluded]);

  const generate = () => { setGenTrigger((t) => t + 1); setGenOpen(true); };
  const clone = async () => {
    const name = window.prompt('Name for the clone:', `${feed.name} copy`);
    if (!name) return;
    const nf = await api.feeds.clone(id, { name });
    navigate(`/feeds/${nf.id}`);
  };
  const copyUrl = (which, url) => { navigator.clipboard.writeText(url); setCopied(which); setTimeout(() => setCopied(''), 1500); };

  if (!feed) {
    return (
      <AppLayout title="Feed">
        {loadError
          ? <p className="text-sm text-red-600">Couldn’t load this feed: {loadError} <button className="underline" onClick={loadFeed}>Retry</button></p>
          : <p className="text-sm text-muted-foreground">Loading…</p>}
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={<span className="flex items-center gap-2">{feed.name}{feed.is_template && <Badge variant="accent"><Layers className="h-3 w-3" /> template</Badge>}</span>}
      description={`${feed.channel_display}${feed.country ? ` · ${feed.country}` : ''} · ${feed.output_format.toUpperCase()}`}
      actions={
        <>
          <Button variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => navigate('/feeds')}><ArrowLeft className="h-4 w-4" /> Feeds</Button>
          <Button variant="secondary" className="gap-1.5" onClick={clone}><Copy className="h-4 w-4" /> Clone</Button>
          <Button className="gap-1.5" onClick={generating ? () => setGenOpen(true) : generate}>
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {generating ? 'Generating…' : 'Generate'}
          </Button>
        </>
      }
    >
      <Card className="mb-5 flex flex-wrap items-center gap-x-8 gap-y-2 px-5 py-3">
        <Meta label="status"><StatusBadge status={feed.status} /></Meta>
        <Meta label="format"><FormatBadge format={feed.output_format} /></Meta>
        <Meta label="quality"><QualityBar score={feed.stats?.quality_score} /></Meta>
        <Meta label="products out"><span className="tnum text-sm font-medium">{feed.stats?.items_out != null ? fmtNum(feed.stats.items_out) : '—'}</span></Meta>
        <div className="ml-auto flex flex-col items-end gap-1.5">
          {feed.gcs_url && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">Public URL</span>
              <code className="max-w-[24rem] truncate rounded-md border border-emerald-600/30 bg-emerald-50 px-2 py-1 font-mono text-[11px] text-emerald-900" title={feed.gcs_url}>{feed.gcs_url}</code>
              <Button variant="outline" size="icon" onClick={() => copyUrl('gcs', feed.gcs_url)} aria-label="Copy public URL">{copied === 'gcs' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
              <a href={feed.gcs_url} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" aria-label="Open public URL"><ExternalLink className="h-4 w-4" /></Button></a>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">{feed.gcs_url ? 'Internal' : 'Feed URL'}</span>
            <code className="max-w-[24rem] truncate rounded-md border border-border bg-muted px-2 py-1 font-mono text-[11px] text-muted-foreground" title={feed.output_url}>{feed.output_url}</code>
            <Button variant="outline" size="icon" onClick={() => copyUrl('local', window.location.origin + feed.output_url)} aria-label="Copy URL">{copied === 'local' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
            {feed.last_generated_at && <a href={feed.output_url} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" aria-label="Open"><ExternalLink className="h-4 w-4" /></Button></a>}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">Example</span>
            <code className="max-w-[24rem] truncate rounded-md border border-border bg-muted px-2 py-1 font-mono text-[11px] text-muted-foreground" title={feed.output_sample_url}>{feed.output_sample_url}</code>
            <Button variant="outline" size="icon" onClick={() => copyUrl('sample', window.location.origin + feed.output_sample_url)} aria-label="Copy example URL">{copied === 'sample' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}</Button>
            {feed.last_generated_at && <a href={feed.output_sample_url} target="_blank" rel="noreferrer"><Button variant="outline" size="icon" aria-label="Open example"><ExternalLink className="h-4 w-4" /></Button></a>}
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="mapping">Mapping</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
          <TabsTrigger value="variables">Variables</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
          <TabsTrigger value="excluded">Excluded</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="general" keepMounted><GeneralTab feed={feed} sources={sources} channelSpec={channelSpec} onSaved={loadFeed} /></TabsContent>
        <TabsContent value="mapping" keepMounted><Card className="p-5"><MappingTab feed={feed} channelFields={channelFields} internalFields={internalFields} onSaved={loadFeed} /></Card></TabsContent>
        <TabsContent value="rules" keepMounted><RulesTab feed={feed} internalFields={internalFields} onSaved={loadFeed} focusRuleId={focusRuleId} /></TabsContent>
        <TabsContent value="variables" keepMounted><VariablesTab feed={feed} onSaved={loadFeed} /></TabsContent>
        <TabsContent value="quality"><QualityPanel feedId={id} report={runReport} sample={preview?.quality} loading={previewLoading && !runReport} onRefresh={() => runPreview()} /></TabsContent>
        <TabsContent value="excluded"><ExcludedPanel
            data={excluded?.error ? null : excluded} loading={excludedLoading} error={excluded?.error} onRefresh={loadExcluded}
            onOpenFeedRule={(rid) => { setFocusRuleId(rid); setTab('rules'); }}
            onOpenMasterRule={(gid, rid) => navigate(`/master-rules?group=${gid}&rule=${rid}`)}
          /></TabsContent>
        <TabsContent value="preview"><PreviewPanel preview={preview} loading={previewLoading} onRefresh={runPreview} format={feed.output_format} /></TabsContent>
      </Tabs>

      <GenerateModal
        feedId={id}
        feedName={feed.name}
        trigger={genTrigger}
        open={genOpen}
        onClose={() => setGenOpen(false)}
        onStart={() => setGenerating(true)}
        onDone={() => { setGenerating(false); loadFeed(); setPreview(null); }}
      />
    </AppLayout>
  );
}

function Meta({ label, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function GeneralTab({ feed, sources, channelSpec, onSaved }) {
  const [form, setFormState] = useState(feed);
  const [saving, setSaving] = useState(false);
  // Re-seed from the server only while the user has no unsaved edits — a reload
  // triggered elsewhere (generate finished, mappings saved) must not wipe them.
  const dirty = useRef(false);
  const setForm = (v) => { dirty.current = true; setFormState(v); };
  useEffect(() => { if (!dirty.current) setFormState(feed); }, [feed]);

  const toggleSource = (sid) => setForm((f) => ({ ...f, sources: f.sources.includes(sid) ? f.sources.filter((x) => x !== sid) : [...f.sources, sid] }));
  const save = async () => {
    setSaving(true);
    try {
      await api.feeds.update(feed.id, {
        name: form.name, country: form.country, currency: form.currency, output_format: form.output_format,
        store_url: form.store_url, locale_filter: form.locale_filter, enabled: form.enabled, is_template: form.is_template,
        sources: form.sources, quality_config: { ...form.quality_config, drop_invalid: !!form.quality_config?.drop_invalid },
      });
      dirty.current = false;
      await onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Card className="space-y-4 p-5 lg:col-span-2">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="BE" /></div>
          <div className="space-y-1.5"><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label>Output format</Label>
            <Select value={form.output_format} onChange={(e) => setForm({ ...form, output_format: e.target.value })}>
              {(channelSpec?.formats || ['xml', 'csv', 'tsv']).map((fmt) => <option key={fmt} value={fmt}>{fmt.toUpperCase()}</option>)}
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Locale filter</Label><Input value={form.locale_filter} onChange={(e) => setForm({ ...form, locale_filter: e.target.value })} placeholder="(any)" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Store URL</Label><Input value={form.store_url} onChange={(e) => setForm({ ...form, store_url: e.target.value })} placeholder="https://lamptwist.be" /></div>
        </div>
        <Toggle label="Drop invalid items" hint="Exclude rows with quality errors from the output." checked={!!form.quality_config?.drop_invalid} onChange={(v) => setForm({ ...form, quality_config: { ...form.quality_config, drop_invalid: v } })} />
        <Toggle label="Mark as template" hint="Templates aren't generated; clone them to create country feeds." checked={!!form.is_template} onChange={(v) => setForm({ ...form, is_template: v })} />
        <Toggle label="Feed enabled" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
        <div className="flex justify-end"><Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Button></div>
      </Card>

      <div className="space-y-5">
        <Card className="p-5">
          <h3 className="text-sm font-semibold">Sources</h3>
          <p className="mb-3 text-xs text-muted-foreground">Which sources feed this output.</p>
          <div className="space-y-2">
            {sources.length === 0 && <p className="text-sm text-muted-foreground">No sources yet.</p>}
            {sources.map((s) => (
              <label key={s.id} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 hover:bg-muted/50">
                <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={form.sources.includes(s.id)} onChange={() => toggleSource(s.id)} />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{s.name}</div>
                  <div className="truncate font-mono text-[11px] text-muted-foreground">{s.type}{s.locale ? ` · ${s.locale.toLowerCase()}` : ''} · {fmtNum(s.item_count)}</div>
                </div>
              </label>
            ))}
          </div>
        </Card>

        <RuleGroupsCard feed={feed} />
      </div>
    </div>
  );
}

// Compact summary of the master-rule groups applied to this feed. By default it
// shows just the assigned groups as colour chips (name · rule count) plus an
// aggregate total, keeping the General tab light. "Manage" expands the full
// list with checkboxes to add/remove this feed from each group's `feeds` M2M
// (persisted immediately, optimistic). Editing the rules themselves stays on
// the Master rules page, linked here.
function RuleGroupsCard({ feed }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => api.masterRuleGroups.list().then(setGroups).catch(() => setGroups([])), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    const ids = g.feeds || [];
    const applies = ids.includes(feed.id);
    const next = applies ? ids.filter((x) => x !== feed.id) : [...ids, feed.id];
    setBusyId(g.id);
    // Optimistic update so the checkbox responds instantly.
    setGroups((gs) => gs.map((x) => (x.id === g.id ? { ...x, feeds: next } : x)));
    try {
      await api.masterRuleGroups.update(g.id, { feeds: next });
    } catch {
      await load(); // revert to server truth on failure
    } finally {
      setBusyId(null);
    }
  };

  const applied = (groups || []).filter((g) => (g.feeds || []).includes(feed.id));
  const totalRules = applied.reduce((n, g) => n + (g.rule_count || 0), 0);

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Master rule groups</h3>
          <p className="text-xs text-muted-foreground">
            {groups === null
              ? 'Global rule sets applied to this feed.'
              : applied.length === 0
                ? 'No groups applied to this feed.'
                : `${applied.length} group${applied.length === 1 ? '' : 's'} · ${fmtNum(totalRules)} rule${totalRules === 1 ? '' : 's'} applied.`}
          </p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => navigate('/master-rules')}>Edit rules <ExternalLink className="h-3.5 w-3.5" /></Button>
      </div>

      {/* Compact aggregate: assigned groups as colour chips with rule counts. */}
      {groups === null
        ? <p className="text-sm text-muted-foreground">Loading…</p>
        : applied.length === 0
          ? <p className="text-xs text-muted-foreground">Use “Manage” to apply a master rule group to this feed.</p>
          : (
            <div className="flex flex-wrap gap-1.5">
              {applied.map((g) => (
                <span
                  key={g.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-[12px]"
                  title={`${g.name} — ${fmtNum(g.rule_count)} rule${g.rule_count === 1 ? '' : 's'}`}
                >
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: g.color || '#2563eb' }} />
                  <span className="font-medium text-foreground">{g.name}</span>
                  <span className="tnum font-mono text-[11px] text-muted-foreground">{fmtNum(g.rule_count)}</span>
                </span>
              ))}
            </div>
          )}

      {/* Click to expand: full list with assign/unassign checkboxes. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground"
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {expanded ? 'Hide groups' : 'Manage groups'}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 border-t border-border pt-3">
          {groups?.length === 0 && <p className="text-sm text-muted-foreground">No master rule groups yet.</p>}
          {groups?.map((g) => {
            const applies = (g.feeds || []).includes(feed.id);
            return (
              <label key={g.id} className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 hover:bg-muted/50">
                <input type="checkbox" className="h-4 w-4 accent-[hsl(var(--primary))]" checked={applies} disabled={busyId === g.id} onChange={() => toggle(g)} />
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: g.color || '#2563eb' }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{g.name}</div>
                  <div className="truncate font-mono text-[11px] text-muted-foreground">{fmtNum(g.rule_count)} rule{g.rule_count === 1 ? '' : 's'}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
      <div><span className="text-sm font-medium">{label}</span>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function VariablesTab({ feed, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(Object.entries(feed.variables || {}).map(([k, v]) => ({ k, v }))); }, [feed.id]); // eslint-disable-line

  const set = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const save = async () => {
    setSaving(true);
    try {
      const variables = {};
      rows.forEach(({ k, v }) => { if (k.trim()) variables[k.trim()] = v; });
      await api.feeds.update(feed.id, { variables });
      await onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Variables</h3>
          <p className="text-xs text-muted-foreground">Substituted into mappings &amp; rules — e.g. <code className="font-mono">{'{f_price}'}</code> → <code className="font-mono">price_compare_at_be</code>, or <code className="font-mono">{'{cc}'}</code> in a URL.</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save variables'}</Button>
      </div>
      <div className="space-y-2">
        {rows.length === 0 && <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">No variables. Add one to vary this feed from its template.</p>}
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={row.k} onChange={(e) => set(i, { k: e.target.value })} placeholder="f_price" className="h-9 w-56 font-mono text-[13px]" />
            <span className="text-muted-foreground">=</span>
            <Input value={row.v} onChange={(e) => set(i, { v: e.target.value })} placeholder="price_compare_at_be" className="h-9 flex-1 font-mono text-[13px]" />
            <Button variant="ghost" size="icon" onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => setRows((r) => [...r, { k: '', v: '' }])}><Plus className="h-4 w-4" /> Add variable</Button>
    </Card>
  );
}

const ISSUE_RECORD_LIMIT = 200;

// One quality issue. Click to drill down to the actual records that trigger it
// (id + the offending field value), fetched on demand from the backend — the
// stored run report only keeps a few example values, not record ids. Each id
// opens the shared RecordModal.
function IssueRow({ feedId, issue, total, onPickRecord }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // { records, count, capped }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const expandable = issue.count > 0 && !!feedId;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading && feedId) {
      setLoading(true); setError(null);
      api.feeds.issueRecords(feedId, { field: issue.field, message: issue.message, limit: ISSUE_RECORD_LIMIT })
        .then(setData)
        .catch(() => setError('Could not load records.'))
        .finally(() => setLoading(false));
    }
  };

  const records = data?.records || [];
  return (
    <>
      <TableRow className={expandable ? 'cursor-pointer hover:bg-muted/50' : ''} onClick={() => expandable && toggle()}>
        <TableCell className="font-mono text-[12px]">
          <span className="inline-flex items-center gap-1.5">
            {expandable
              ? (open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />)
              : <span className="w-3.5" />}
            {issue.field}
          </span>
        </TableCell>
        <TableCell><Badge variant={issue.severity === 'error' ? 'destructive' : 'warning'}>{issue.severity}</Badge></TableCell>
        <TableCell>{issue.message}</TableCell>
        <TableCell className="tnum text-right">{fmtNum(issue.count)}</TableCell>
        <TableCell className="tnum text-right text-muted-foreground">{total ? ((100 * issue.count) / total).toFixed(1) : '0'}%</TableCell>
      </TableRow>
      {open && expandable && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={5} className="px-5 py-3">
            {loading && records.length === 0 && <p className="py-2 text-center text-[11px] text-muted-foreground">Loading records…</p>}
            {error && <p className="py-2 text-center text-[11px] text-rose-600">{error}</p>}
            {!loading && !error && records.length === 0 && <p className="py-2 text-center text-[11px] text-muted-foreground">No matching records found in the current output.</p>}
            {records.length > 0 && (
              <>
                <div className="mb-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Click an id to inspect the full record</span>
                  <span className="tnum">{fmtNum(data.count)} record{data.count === 1 ? '' : 's'}{data.count > records.length ? ` · showing first ${records.length}` : ''}</span>
                </div>
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full table-fixed text-[11px]">
                    <thead>
                      <tr className="bg-muted/60 text-left text-muted-foreground">
                        <th className="w-40 px-2 py-1 font-medium">id</th>
                        <th className="px-2 py-1 font-medium">{issue.field}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r, i) => (
                        <tr key={i} className="border-t border-border/60 align-top">
                          <td className="px-2 py-1">
                            <button onClick={(e) => { e.stopPropagation(); onPickRecord(r.id); }} className="font-mono text-sky-600 hover:underline" title="Open record">{r.id}</button>
                          </td>
                          <td className="px-2 py-1 font-mono break-words">{Array.isArray(r.value) ? r.value.join(' | ') : (r.value === '' || r.value == null ? '∅' : String(r.value))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function QualityPanel({ feedId, report, sample, loading, onRefresh }) {
  const q = report || sample;
  const isFull = !!report;
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);

  // Open the rich record modal for an offending item id.
  const openRecord = async (itemId) => {
    if (!feedId) return;
    setRecLoading(true);
    setRec({ id: itemId }); // show the modal immediately with the id
    try {
      const res = await api.feeds.preview(feedId, { q: String(itemId), limit: 1 });
      const row = (res.rows || []).find((r) => String(r.id) === String(itemId)) || res.rows?.[0];
      setRec(row || { id: itemId });
    } catch {
      setRec({ id: itemId });
    } finally {
      setRecLoading(false);
    }
  };

  if (!q) {
    if (loading) return <Card className="p-10 text-center text-sm text-muted-foreground">Running checks…</Card>;
    return <Card className="p-10 text-center"><Button variant="outline" onClick={onRefresh} className="gap-1.5"><RefreshCw className="h-4 w-4" /> Run quality check</Button></Card>;
  }
  // Guard against partial/older report shapes (a regenerated feed's stored run
  // report may omit fields older reports carried). Never assume `issues` etc.
  const issues = Array.isArray(q.issues) ? q.issues : [];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-4"><p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Score</p><div className="mt-2"><QualityBar score={q.score} /></div></Card>
        <Card className="p-4"><p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{isFull ? 'Records (all)' : 'Rows (sample)'}</p><p className="tnum mt-1 text-2xl font-semibold">{fmtNum(q.total)}</p></Card>
        <Card className="p-4"><p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Errors</p><p className="tnum mt-1 text-2xl font-semibold text-rose-600">{fmtNum(q.errors)}</p></Card>
        <Card className="p-4"><p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Warnings</p><p className="tnum mt-1 text-2xl font-semibold text-amber-600">{fmtNum(q.warnings)}</p></Card>
      </div>
      <Card>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Issues <span className="font-mono text-[11px] text-muted-foreground">{isFull ? '· all records (last generation) — click a row to drill down' : '· preview sample — generate for full'}</span></h3>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={onRefresh}><RefreshCw className="h-4 w-4" /> Refresh</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Severity</TableHead><TableHead>Message</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">% of records</TableHead></TableRow></TableHeader>
          <TableBody>
            {issues.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-emerald-600">No issues found.</TableCell></TableRow>}
            {issues.map((issue, i) => (
              <IssueRow key={i} feedId={feedId} issue={issue} total={q.total || 0} onPickRecord={openRecord} />
            ))}
          </TableBody>
        </Table>
      </Card>
      <RecordModal row={recLoading && !rec?.title ? { ...rec, title: 'Loading record…' } : rec} onClose={() => setRec(null)} />
    </div>
  );
}

// The base (PIM) products that did NOT make it into the feed, each with the
// reason it was dropped. Read instantly from the latest generation run's stored
// report (recorded during generation). Header summary + a clickable reason
// breakdown that filters the thumbnail grid of sample products below.
const SOURCE_BADGE = {
  feed_rule: ['Feed rule', 'bg-sky-500/10 text-sky-700'],
  master_rule: ['Master rule', 'bg-violet-500/10 text-violet-700'],
  market: ['Shopify market', 'bg-amber-500/10 text-amber-700'],
  channel: ['Channel check', 'bg-rose-500/10 text-rose-700'],
  unknown_rule: ['Rule (not found)', 'bg-muted text-muted-foreground'],
};

// Where one excluded-reason comes from: the rule (feed or master — with its
// group and every feed/country the group applies to), the Shopify market
// exclusions, or a channel check — with a link to edit the rule.
function ReasonSource({ src, onOpenFeedRule, onOpenMasterRule }) {
  if (!src) return null;
  if (src.kind === 'feed_rule') {
    return (
      <div className="space-y-2 text-[12px]">
        <p><span className="text-muted-foreground">Feed rule of this feed only:</span> <span className="font-medium">{src.name}</span>{!src.enabled && <span className="ml-1.5 text-rose-600">(disabled)</span>}</p>
        <p className="font-mono text-[11px] text-muted-foreground">IF {src.condition || '—'}</p>
        <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={() => onOpenFeedRule(src.rule_id)}><ExternalLink className="h-3.5 w-3.5" /> Edit rule</Button>
      </div>
    );
  }
  if (src.kind === 'master_rule') {
    return (
      <div className="space-y-3 text-[12px]">
        {src.rules.map((r) => (
          <div key={r.rule_id} className="space-y-1.5">
            <p>
              <span className="text-muted-foreground">Master rule in group</span> <span className="font-medium">{r.group_name}</span>
              {(!r.enabled || !r.group_enabled) && <span className="ml-1.5 text-rose-600">({!r.group_enabled ? 'group off' : 'rule disabled'})</span>}
              <span className="text-muted-foreground"> · applies to {r.feeds.length} feed{r.feeds.length === 1 ? '' : 's'}</span>
            </p>
            <p className="font-mono text-[11px] text-muted-foreground">IF {r.condition || '—'}</p>
            <div className="flex flex-wrap gap-1">
              {r.countries.map((cc) => <span key={cc} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">{cc}</span>)}
            </div>
            <details className="text-[11px] text-muted-foreground">
              <summary className="cursor-pointer">Feeds in this group</summary>
              <p className="mt-1">{r.feeds.map((f) => `${f.name}${f.language ? ` (${f.language})` : ''}`).join(' · ')}</p>
            </details>
            <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={() => onOpenMasterRule(r.group_id, r.rule_id)}><ExternalLink className="h-3.5 w-3.5" /> Edit master rule</Button>
          </div>
        ))}
      </div>
    );
  }
  return <p className="text-[12px] text-muted-foreground">{src.kind === 'market' ? `Shopify market ${src.country}: ` : ''}{src.detail || 'No matching rule found by this name.'}</p>;
}

function ExcludedPanel({ data, loading, error, onRefresh, onOpenFeedRule, onOpenMasterRule }) {
  const [rec, setRec] = useState(null);
  const [openSource, setOpenSource] = useState(null);
  const [activeReason, setActiveReason] = useState(null); // null = show all
  const [query, setQuery] = useState('');

  if (!data) {
    if (loading) return <Card className="p-10 text-center text-sm text-muted-foreground">Loading excluded products…</Card>;
    if (error) return <Card className="p-10 text-center"><p className="mb-3 text-sm text-rose-600">Could not load excluded products.</p><Button variant="outline" onClick={onRefresh} className="gap-1.5"><RefreshCw className="h-4 w-4" /> Retry</Button></Card>;
    return <Card className="p-10 text-center"><Button variant="outline" onClick={onRefresh} className="gap-1.5"><RefreshCw className="h-4 w-4" /> Load excluded products</Button></Card>;
  }

  const { base_total = 0, output_total = 0, excluded_total = 0, by_reason = {}, sample = [], stale = false, reason_sources = {} } = data;
  const reasons = Object.entries(by_reason); // already sorted desc by the backend
  const pct = base_total ? ((100 * excluded_total) / base_total).toFixed(1) : '0';

  // Client-side filter of the sample grid by the selected reason + search query
  // (matches product title or id — searches the loaded sample).
  const byReason = activeReason ? sample.filter((p) => p.reason === activeReason) : sample;
  const q = query.trim().toLowerCase();
  const shown = q
    ? byReason.filter((p) => (p.title || '').toLowerCase().includes(q) || String(p.id).includes(q))
    : byReason;
  // Full count for the active reason (from by_reason, not the possibly-capped sample).
  const activeFullCount = activeReason ? (by_reason[activeReason] || 0) : excluded_total;
  const moreThanShown = activeFullCount > shown.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <p className="text-sm">
          <span className="tnum text-lg font-semibold">{fmtNum(excluded_total)}</span>
          <span className="text-muted-foreground"> of </span>
          <span className="tnum font-semibold">{fmtNum(base_total)}</span>
          <span className="text-muted-foreground"> base products excluded</span>
          <span className="text-muted-foreground"> · {pct}%</span>
        </p>
        <div className="ml-auto flex items-center gap-3">
          <Count label="base" value={base_total} />
          <Count label="in feed" value={output_total} />
          <Count label="excluded" value={excluded_total} />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={onRefresh} disabled={loading}><RefreshCw className="h-4 w-4" /> Refresh</Button>
        </div>
      </div>

      {stale && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          Computed on demand — this feed hasn’t been generated since exclusion tracking shipped. Regenerate it to make this tab load instantly.
        </p>
      )}

      <Card>
        <div className="border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">By reason <span className="font-mono text-[11px] text-muted-foreground">· click a reason to filter the products below</span></h3>
        </div>
        {reasons.length === 0 ? (
          <p className="py-8 text-center text-sm text-emerald-600">Every base product makes it into this feed — nothing excluded.</p>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Reason</TableHead><TableHead>Source</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">% of base</TableHead></TableRow></TableHeader>
            <TableBody>
              <TableRow
                className={`cursor-pointer hover:bg-muted/50 ${activeReason === null ? 'bg-muted/60' : ''}`}
                onClick={() => setActiveReason(null)}
              >
                <TableCell className="font-medium">{activeReason === null ? '▸ ' : ''}All reasons</TableCell>
                <TableCell />
                <TableCell className="tnum text-right">{fmtNum(excluded_total)}</TableCell>
                <TableCell className="tnum text-right text-muted-foreground">{pct}%</TableCell>
              </TableRow>
              {reasons.map(([reason, count]) => {
                const active = activeReason === reason;
                const src = reason_sources[reason];
                const [label, cls] = SOURCE_BADGE[src?.kind] || ['—', 'bg-muted text-muted-foreground'];
                const open = openSource === reason;
                return (
                  <React.Fragment key={reason}>
                    <TableRow
                      className={`cursor-pointer hover:bg-muted/50 ${active ? 'bg-muted/60' : ''}`}
                      onClick={() => setActiveReason(active ? null : reason)}
                    >
                      <TableCell className={active ? 'font-medium' : ''}>{active ? '▸ ' : ''}{reason}</TableCell>
                      <TableCell>
                        <button
                          onClick={(e) => { e.stopPropagation(); setOpenSource(open ? null : reason); }}
                          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${cls} hover:opacity-80`}
                          title="Where does this come from?"
                        >
                          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}{label}
                        </button>
                      </TableCell>
                      <TableCell className="tnum text-right">{fmtNum(count)}</TableCell>
                      <TableCell className="tnum text-right text-muted-foreground">{base_total ? ((100 * count) / base_total).toFixed(1) : '0'}%</TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={4} className="py-3">
                          <ReasonSource src={src} onOpenFeedRule={onOpenFeedRule} onOpenMasterRule={onOpenMasterRule} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {sample.length > 0 && (
        <Card className="p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              {activeReason ? 'Excluded products for this reason' : 'Excluded products'}
              <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                {q
                  ? `· ${fmtNum(shown.length)} match${shown.length === 1 ? '' : 'es'}`
                  : `· showing ${fmtNum(shown.length)}${moreThanShown ? ` of ${fmtNum(activeFullCount)}` : ''}`}
              </span>
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search title or id…"
                  className="h-8 w-56 pl-8 text-xs"
                />
              </div>
              {activeReason && (
                <Button variant="outline" size="sm" onClick={() => setActiveReason(null)}>Clear filter</Button>
              )}
            </div>
          </div>
          {shown.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{q ? 'No products match your search.' : 'No sampled products for this reason.'}</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]" />
                    <TableHead>Product</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="w-[90px] text-right">Shopify</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setRec({ id: p.id, title: p.title, image_link: p.image })}>
                      <TableCell>
                        <div className="h-9 w-9 shrink-0 overflow-hidden rounded border border-border bg-muted">
                          {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md truncate text-[13px] font-medium">{p.title || '—'}</div>
                        <div className="truncate font-mono text-[10px] text-muted-foreground">{p.id}</div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-block rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700">{p.reason}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        {p.shopify_admin_url && (
                          <a href={p.shopify_admin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[12px] font-medium text-sky-600 hover:underline">Open ↗</a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}
      <RecordModal row={rec} onClose={() => setRec(null)} />
    </div>
  );
}

function PreviewPanel({ preview, loading, onRefresh, format }) {
  const [view, setView] = useState('table');
  const [q, setQ] = useState('');
  const [rec, setRec] = useState(null);
  if (loading) return <Card className="p-10 text-center text-sm text-muted-foreground">Building preview…</Card>;
  if (!preview) return <Card className="p-10 text-center"><Button variant="outline" onClick={() => onRefresh()} className="gap-1.5"><RefreshCw className="h-4 w-4" /> Run preview</Button></Card>;
  const { counts, columns, rows } = preview;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Count label="in" value={counts.items_in} />
        <Count label="excluded" value={counts.items_excluded} />
        <Count label="out" value={counts.items_out} />
        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={(e) => { e.preventDefault(); onRefresh(q.trim() || undefined); }} className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all items: id / title / sku / brand…" className="h-9 w-72 pl-8" />
          </form>
          <div className="flex rounded-md border border-border p-0.5">
            {['table', 'raw'].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`rounded px-2.5 py-1 text-[12px] font-medium ${view === v ? 'bg-muted text-foreground' : 'text-muted-foreground'}`}>
                {v === 'raw' ? format.toUpperCase() : 'Table'}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => { setQ(''); onRefresh(); }}><RefreshCw className="h-4 w-4" /> Reset</Button>
        </div>
      </div>
      {q && <p className="text-[11px] text-muted-foreground">Searching <span className="font-medium">all items</span> for “{q}” — {rows.length} match{rows.length === 1 ? '' : 'es'} · click a row to inspect</p>}
      {view === 'table' ? (
        <Card>
          <Table>
            <TableHeader><TableRow>{columns.map((c) => <TableHead key={c} className="font-mono normal-case tracking-normal">{c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={Math.max(columns.length, 1)} className="py-8 text-center text-muted-foreground">No rows. Check sources, mapping and rules.</TableCell></TableRow>}
              {rows.map((row, i) => (
                <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => setRec(row)}>
                  {columns.map((c) => <TableCell key={c} className="max-w-[220px] truncate font-mono text-[11px]">{Array.isArray(row[c]) ? row[c].join(' | ') : row[c]}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="p-0"><pre className="max-h-[480px] overflow-auto rounded-lg p-4 font-mono text-[11px] leading-relaxed">{preview.sample_output}</pre></Card>
      )}
      <RecordModal row={rec} columns={columns} onClose={() => setRec(null)} />
    </div>
  );
}

function Count({ label, value }) {
  return <span className="text-sm text-muted-foreground"><span className="font-mono text-[10px] uppercase tracking-wider">{label}</span> <span className="tnum ml-1 font-medium text-foreground">{fmtNum(value)}</span></span>;
}
